import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import notifications from "../../common/Components/Notifications/Notification";
import { canManageKarkhana } from "../../helpers/karkhanaEnabled";
import {
  approveTimeRate,
  createTimeActivity,
  createTimeRate,
  discardTimeRate,
  getTimeRateAudit,
  listTimeActivities,
  listTimeRates,
  previewTimePay,
  saveTimeRate,
  searchKarkhanaWorkers,
} from "../../services/karkhanaApi";
import "../gi-registry/gi-registry.scss";
import { fmtDay, rupees, todayIso } from "./rates/rateModel";
import {
  byActivity,
  describeRate,
  emptyForm,
  formBody,
  formFrom,
  hoursLabel,
  payWorking,
  RateForm,
  rateRupees,
  rateState,
  splitDayRate,
  TimeRate,
  toMinutes,
} from "./rates/timeModel";
import "./workers/karkhana-workers.scss";
import "./rates/karkhana-rates.scss";
import PageHeader from "./ui/PageHeader";
import SearchField from "./ui/SearchField";
import { confirmDialog } from "./ui/dialog";
import { kp } from "./karkhanaPaths";

const readRole = () => {
  try {
    return JSON.parse(localStorage.getItem("authUser") || "null")?.users?.role;
  } catch {
    return undefined;
  }
};

const call = async (fn: () => Promise<any>, ok?: string): Promise<any | null> => {
  try {
    const res: any = await fn();
    if (!res?.success) {
      notifications({ title: "Error", message: res?.message || "Something went wrong", type: "danger" });
      return null;
    }
    if (ok) notifications({ title: "Done", message: ok, type: "success" });
    return res.data;
  } catch {
    notifications({ title: "Error", message: "Could not reach the server", type: "danger" });
    return null;
  }
};

const STATE_CHIP: Record<string, { label: string; cls: string }> = {
  draft: { label: "Draft", cls: "is-review" },
  current: { label: "In use", cls: "is-ok" },
  upcoming: { label: "Starts later", cls: "is-info" },
  ended: { label: "Ended", cls: "" },
};

type Editing = { personId?: string; activityId?: string; title: string; allowOvertime: boolean; form: RateForm };

/** One row: who/what the rate is for, its current or upcoming rate, its draft, and actions. */
const RateRow = ({
  title,
  rates,
  today,
  canWrite,
  canApprove,
  onAdd,
  onEdit,
  onApprove,
  onDiscard,
  emptyText = "No rate",
}: {
  title: React.ReactNode;
  emptyText?: string;
  rates: TimeRate[];
  today: string;
  canWrite: boolean;
  canApprove: boolean;
  onAdd: () => void;
  onEdit: (r: TimeRate) => void;
  onApprove: (r: TimeRate) => void;
  onDiscard: (r: TimeRate) => void;
}) => {
  const shown = rates.filter((r) => rateState(r, today) !== "ended");
  const ended = rates.filter((r) => rateState(r, today) === "ended");
  const hasDraft = rates.some((r) => r.status === "DRAFT");
  return (
    <div className="kt-row">
      <div className="kt-row__title">{title}</div>
      <div className="kt-row__rates">
        {shown.length === 0 ? <span className="kw-muted">{emptyText}</span> : null}
        {shown.map((r) => {
          const st = STATE_CHIP[rateState(r, today)];
          return (
            <div key={r.id} className="kt-rate">
              <span className={`kw-chip ${st.cls}`}>{st.label}</span>
              <span>{r.amount ? describeRate(r) : <em className="kw-muted">no amount yet</em>}</span>
              <span className="kw-muted">from {fmtDay(r.effectiveFrom)}</span>
              {r.status === "DRAFT" && canWrite ? (
                <span className="kt-rate__actions">
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onEdit(r)}>
                    Edit
                  </button>
                  <button type="button" className="btn btn-link btn-sm p-0" onClick={() => onDiscard(r)}>
                    Discard
                  </button>
                  {canApprove ? (
                    <Button size="sm" className="ResetBtn" onClick={() => onApprove(r)} disabled={!r.amount}>
                      Approve
                    </Button>
                  ) : (
                    <span className="kw-muted">Waiting for the admin</span>
                  )}
                </span>
              ) : null}
            </div>
          );
        })}
        {ended.length ? (
          <span className="kw-muted kt-ended">
            Earlier: {ended.map((r) => `₹${r.hourlyRate}/h until ${fmtDay(r.effectiveTo)}`).join(" · ")}
          </span>
        ) : null}
      </div>
      {canWrite && !hasDraft ? (
        <Button size="sm" outline onClick={onAdd}>
          {shown.length ? "New rate" : "Set rate"}
        </Button>
      ) : null}
    </div>
  );
};

const KarkhanaTimeRatesPage = () => {
  const history = useHistory();
  const location = useLocation();
  const role = readRole();
  const canWrite = canManageKarkhana(role) && role !== "SUPER_ADMIN";
  const [activities, setActivities] = useState<any[]>([]);
  const [defaults, setDefaults] = useState<TimeRate[]>([]);
  const [canApprove, setCanApprove] = useState(false);
  const [today, setToday] = useState(todayIso());
  const [person, setPerson] = useState<any>(null);
  const [personRates, setPersonRates] = useState<TimeRate[]>([]);
  const [audit, setAudit] = useState<any[]>([]);
  const [q, setQ] = useState("");
  const [found, setFound] = useState<any[]>([]);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [busy, setBusy] = useState(false);
  const [newActivity, setNewActivity] = useState("");
  const [tryDate, setTryDate] = useState(todayIso());
  const [stretches, setStretches] = useState<{ activityId: string; hours: string }[]>([{ activityId: "", hours: "" }]);
  const [result, setResult] = useState<any>(null);
  const timer = useRef<number | undefined>(undefined);

  const loadStudio = useCallback(async () => {
    const [acts, rates]: any[] = await Promise.all([listTimeActivities(), listTimeRates()]);
    setActivities(acts?.success ? acts.data || [] : []);
    if (rates?.success) {
      setDefaults(rates.data.rates || []);
      setCanApprove(!!rates.data.canApprove);
      setToday(rates.data.today || todayIso());
    }
  }, []);

  const loadPerson = useCallback(async (p: any) => {
    setPerson(p);
    setResult(null);
    if (!p) return;
    const [rates, hist]: any[] = await Promise.all([listTimeRates(p.id), getTimeRateAudit(p.id)]);
    setPersonRates(rates?.success ? rates.data.rates || [] : []);
    setAudit(hist?.success ? hist.data || [] : []);
  }, []);

  useEffect(() => {
    if (!canManageKarkhana(role)) {
      history.replace(kp.home());
      return;
    }
    loadStudio();
    const code = new URLSearchParams(location.search).get("code");
    if (code) {
      searchKarkhanaWorkers({ q: code, limit: 20 }).then((res: any) => {
        const p = (res?.data?.docs || []).find((w: any) => String(w.workerCode).toUpperCase() === code.toUpperCase());
        if (p) loadPerson(p);
      });
    }
  }, [role, history, location.search, loadStudio, loadPerson]);

  useEffect(() => {
    window.clearTimeout(timer.current);
    if (!q.trim()) {
      setFound([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      const res: any = await searchKarkhanaWorkers({ q: q.trim(), limit: 8 });
      setFound(res?.success ? res.data?.docs || [] : []);
    }, 200);
  }, [q]);

  const activeActivities = activities.filter((a) => a.active !== false);
  const defaultsByActivity = useMemo(() => byActivity(defaults), [defaults]);
  const personByActivity = useMemo(() => byActivity(personRates), [personRates]);

  const refresh = async () => {
    await loadStudio();
    if (person) await loadPerson(person);
  };

  const startEdit = (target: { personId?: string; activityId?: string; title: string }, r?: TimeRate) => {
    setEditing({
      ...target,
      allowOvertime: !!target.personId && !target.activityId,
      form: r ? formFrom(r) : emptyForm(),
    });
  };

  const saveEditing = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const body = formBody(editing.form, editing.allowOvertime);
      const data = editing.form.id
        ? await call(() => saveTimeRate(editing.form.id!, body), "Draft saved")
        : await call(
            () => createTimeRate({ ...body, personId: editing.personId, activityId: editing.activityId }),
            "Draft saved. The admin approves it."
          );
      if (data) {
        setEditing(null);
        await refresh();
      }
    } finally {
      setBusy(false);
    }
  };

  const approve = async (r: TimeRate) => {
    if (!(await confirmDialog({ title: `Approve ${describeRate(r)} from ${fmtDay(r.effectiveFrom)}? Approved rates can't be changed.` }))) return;
    if (await call(() => approveTimeRate(r.id), "Rate approved")) await refresh();
  };

  const discard = async (r: TimeRate) => {
    if (!(await confirmDialog({ title: "Discard this draft?" }))) return;
    if (await call(() => discardTimeRate(r.id), "Draft discarded")) await refresh();
  };

  const addActivity = async () => {
    if (!newActivity.trim()) return;
    if (await call(() => createTimeActivity({ name: newActivity.trim() }), "Activity added")) {
      setNewActivity("");
      await loadStudio();
    }
  };

  const tryPay = async () => {
    if (!person) return;
    const list = stretches
      .map((s) => ({ activityId: s.activityId, minutes: toMinutes(s.hours) }))
      .filter((s) => s.activityId && s.minutes);
    if (!list.length) {
      notifications({ title: "Error", message: "Add an activity and how long", type: "danger" });
      return;
    }
    const data = await call(() => previewTimePay({ personId: person.id, date: tryDate, stretches: list }));
    if (data) setResult(data);
  };

  if (!canManageKarkhana(role)) return null;

  const rowProps = {
    today,
    canWrite,
    canApprove,
    onApprove: approve,
    onDiscard: discard,
  };

  return (
    <div className="gi-registry-page kw-page">
      <PageHeader title="Time rates" back={kp.settings()} />
      <p className="kw-lede">
        For work paid by the hour or day. A person can earn a different rate for each activity. HKP looks for the
        person's rate for that activity, then their general rate, then the studio's rate for the activity.
      </p>

      <div className="kw-card">
        <h3>A person's rates</h3>
        {person ? (
          <div className="kw-toolbar">
            <span className="kw-chip is-info">
              <span className="kw-code">{person.workerCode}</span>&nbsp;{person.name}
            </span>
            <Button size="sm" outline onClick={() => { setPerson(null); setQ(""); }}>
              Choose someone else
            </Button>
          </div>
        ) : (
          <>
            <SearchField
              value={q}
              onChange={setQ}
              placeholder="Search by code or name, e.g. J_600 or Lal Babu"
              label="Find a worker"
              autoCapitalize="characters"
            />
            {found.length ? (
              <div className="kr-design-options">
                {found.map((w) => (
                  <button key={w.id} type="button" className="kr-design-option kw-press" onClick={() => loadPerson(w)}>
                    <span className="kw-code">{w.workerCode}</span> {w.name}
                  </button>
                ))}
              </div>
            ) : null}
          </>
        )}

        {person ? (
          <>
            <RateRow
              {...rowProps}
              title={
                <>
                  <strong>General rate</strong>
                  <span className="kw-muted">Any activity without its own rate. Overtime is set here.</span>
                </>
              }
              rates={personByActivity.get("") || []}
              onAdd={() => startEdit({ personId: person.id, title: `${person.name}: general rate` })}
              onEdit={(r) => startEdit({ personId: person.id, title: `${person.name}: general rate` }, r)}
            />
            {activeActivities.map((a) => (
              <RateRow
                key={a.id}
                {...rowProps}
                title={<strong>{a.name}</strong>}
                emptyText={
                  (personByActivity.get("") || []).some((r) => rateState(r, today) === "current")
                    ? "Uses the general rate"
                    : a.defaultRate
                    ? `Uses the studio rate ₹${a.defaultRate.hourlyRate}/h`
                    : "No rate"
                }
                rates={personByActivity.get(a.id) || []}
                onAdd={() => startEdit({ personId: person.id, activityId: a.id, title: `${person.name}: ${a.name}` })}
                onEdit={(r) => startEdit({ personId: person.id, activityId: a.id, title: `${person.name}: ${a.name}` }, r)}
              />
            ))}

            <div className="kt-try">
              <h4>Try a day's pay</h4>
              <div className="kw-toolbar">
                <Input type="date" value={tryDate} onChange={(e) => setTryDate(e.target.value)} aria-label="Day" style={{ maxWidth: 180 }} />
              </div>
              {stretches.map((s, i) => (
                <div key={i} className="kw-toolbar">
                  <Input
                    type="select"
                    value={s.activityId}
                    aria-label="Activity"
                    style={{ maxWidth: 260 }}
                    onChange={(e) => setStretches(stretches.map((x, k) => (k === i ? { ...x, activityId: e.target.value } : x)))}
                  >
                    <option value="">Activity</option>
                    {activeActivities.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Input>
                  <Input
                    value={s.hours}
                    inputMode="decimal"
                    placeholder="Hours, e.g. 2:30"
                    aria-label="Hours"
                    style={{ maxWidth: 160 }}
                    onChange={(e) => setStretches(stretches.map((x, k) => (k === i ? { ...x, hours: e.target.value } : x)))}
                  />
                  {stretches.length > 1 ? (
                    <button type="button" className="btn btn-link btn-sm p-0" onClick={() => setStretches(stretches.filter((_, k) => k !== i))}>
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
              <div className="kw-toolbar">
                <Button size="sm" outline onClick={() => setStretches([...stretches, { activityId: "", hours: "" }])}>
                  Add activity
                </Button>
                <Button size="sm" className="ResetBtn" onClick={tryPay}>
                  Work out pay
                </Button>
              </div>
              {result ? (
                <div className="kt-result">
                  {result.lines.map((l: any, i: number) => (
                    <div key={i} className="kt-result__line">
                      <span>
                        {l.activityName} · {hoursLabel(l.minutes)}
                        {l.overtimeMinutes ? ` (${hoursLabel(l.overtimeMinutes)} overtime)` : ""}
                      </span>
                      <span className="kw-muted">
                        {l.status === "OK"
                          ? `${payWorking(l.minutes - (l.overtimeMinutes || 0), l.hourlyRate, l.minuteRate) || `₹${l.hourlyRate}/h`}${
                              l.overtimeRate ? `, overtime ₹${Number(l.overtimeRate).toFixed(2)}/h` : ""
                            } · ${
                              l.source === "PERSON_ACTIVITY" ? "own rate" : l.source === "PERSON_GENERAL" ? "general rate" : "studio rate"
                            }`
                          : "No rate set"}
                      </span>
                      <span className="kr-num">{l.status === "OK" ? rupees(l.amount) : <span className="kw-chip is-review">Rate not set</span>}</span>
                    </div>
                  ))}
                  <div className="kt-result__line kt-result__total">
                    <span>{hoursLabel(result.totalMinutes)} in total</span>
                    <span />
                    <span className="kr-num">{rupees(result.total)}</span>
                  </div>
                  {!result.complete ? <p className="kw-muted">Some time has no rate, so it isn't counted.</p> : null}
                </div>
              ) : null}
            </div>

            {audit.length ? (
              <div style={{ marginTop: 16 }}>
                <h4>History</h4>
                <ul className="kr-history">
                  {audit.slice(0, 20).map((a) => (
                    <li key={a.id}>
                      <strong>{a.action.charAt(0) + a.action.slice(1).toLowerCase().replace(/_/g, " ")}</strong>
                      <span className="kw-muted">
                        {" "}
                        · {a.byName} · {new Date(a.at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </span>
                      <span className="kr-change">{a.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="kw-card">
        <h3>Studio rates per activity</h3>
        <p className="kw-muted">Used for anyone who has no rate of their own.</p>
        {activeActivities.map((a) => (
          <RateRow
            key={a.id}
            {...rowProps}
            title={<strong>{a.name}</strong>}
            rates={defaultsByActivity.get(a.id) || []}
            onAdd={() => startEdit({ activityId: a.id, title: `Studio rate: ${a.name}` })}
            onEdit={(r) => startEdit({ activityId: a.id, title: `Studio rate: ${a.name}` }, r)}
          />
        ))}
        {canWrite ? (
          <div className="kw-toolbar" style={{ marginTop: 12 }}>
            <Input
              value={newActivity}
              onChange={(e) => setNewActivity(e.target.value)}
              placeholder="New activity, e.g. Bobbin winding"
              aria-label="New activity name"
              style={{ maxWidth: 320 }}
            />
            <Button size="sm" outline disabled={!newActivity.trim()} onClick={addActivity}>
              Add activity
            </Button>
          </div>
        ) : null}
      </div>

      <Modal isOpen={!!editing} toggle={() => setEditing(null)} modalClassName="kw-modal">
        <ModalHeader toggle={() => setEditing(null)}>{editing?.title}</ModalHeader>
        {editing ? (
          <ModalBody>
            <Label>Paid</Label>
            <div className="kw-toolbar">
              {(["HOURLY", "DAILY"] as const).map((b) => (
                <button
                  key={b}
                  type="button"
                  className={`kw-filter${editing.form.basis === b ? " is-active" : ""}`}
                  onClick={() => setEditing({ ...editing, form: { ...editing.form, basis: b } })}
                >
                  {b === "HOURLY" ? "Per hour" : "Per day"}
                </button>
              ))}
            </div>
            <Label for="kt-amount">{editing.form.basis === "DAILY" ? "₹ per day" : "₹ per hour"}</Label>
            <Input
              id="kt-amount"
              inputMode="decimal"
              value={editing.form.amount}
              onChange={(e) => setEditing({ ...editing, form: { ...editing.form, amount: e.target.value } })}
              autoFocus
            />
            {editing.form.basis === "DAILY" ? (
              <>
                <Label className="mt-2" for="kt-hours">
                  Hours in a paid day
                </Label>
                <Input
                  id="kt-hours"
                  inputMode="decimal"
                  value={editing.form.hoursPerDay}
                  onChange={(e) => setEditing({ ...editing, form: { ...editing.form, hoursPerDay: e.target.value } })}
                />
                {Number(editing.form.amount) > 0 && Number(editing.form.hoursPerDay) > 0 ? (
                  <p className="kw-muted mt-1">
                    = {rateRupees(splitDayRate(Number(editing.form.amount), Number(editing.form.hoursPerDay)).hourly)} per hour ·{" "}
                    {rateRupees(splitDayRate(Number(editing.form.amount), Number(editing.form.hoursPerDay)).perMinute)} per minute
                  </p>
                ) : null}
              </>
            ) : null}
            <Label className="mt-2" for="kt-from">
              Applies from
            </Label>
            <Input
              id="kt-from"
              type="date"
              value={editing.form.effectiveFrom}
              onChange={(e) => setEditing({ ...editing, form: { ...editing.form, effectiveFrom: e.target.value } })}
            />
            {editing.allowOvertime ? (
              <>
                <label className="mt-3" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input
                    type="checkbox"
                    checked={editing.form.overtimeOn}
                    onChange={(e) => setEditing({ ...editing, form: { ...editing.form, overtimeOn: e.target.checked } })}
                  />
                  Pay overtime
                </label>
                {editing.form.overtimeOn ? (
                  <div className="kw-toolbar">
                    <span>After</span>
                    <Input
                      style={{ width: 70 }}
                      inputMode="decimal"
                      aria-label="Overtime after hours"
                      value={editing.form.overtimeAfterHours}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, overtimeAfterHours: e.target.value } })}
                    />
                    <span>hours a day, pay</span>
                    <Input
                      style={{ width: 80 }}
                      inputMode="decimal"
                      aria-label="Overtime value"
                      value={editing.form.overtimeValue}
                      onChange={(e) => setEditing({ ...editing, form: { ...editing.form, overtimeValue: e.target.value } })}
                    />
                    <Input
                      type="select"
                      style={{ width: "auto" }}
                      aria-label="Overtime type"
                      value={editing.form.overtimeMode}
                      onChange={(e) =>
                        setEditing({ ...editing, form: { ...editing.form, overtimeMode: e.target.value as "RATE" | "MULTIPLIER" } })
                      }
                    >
                      <option value="MULTIPLIER">× the usual rate</option>
                      <option value="RATE">₹ per hour</option>
                    </Input>
                  </div>
                ) : null}
              </>
            ) : null}
            <Label className="mt-2" for="kt-note">
              Note (optional)
            </Label>
            <Input
              id="kt-note"
              value={editing.form.note}
              onChange={(e) => setEditing({ ...editing, form: { ...editing.form, note: e.target.value } })}
            />
            <p className="kw-muted mt-2" style={{ marginBottom: 0 }}>
              Saved as a draft. The studio admin approves it; after that it can't be changed.
            </p>
          </ModalBody>
        ) : null}
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Button className="ResetBtn" disabled={busy || !editing?.form.amount} onClick={saveEditing}>
            {busy ? "Saving…" : "Save draft"}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default KarkhanaTimeRatesPage;
